/* eslint-disable import/no-unused-modules */
import * as React from "react";
import { Slot as SlotPrimitive } from "radix-ui";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "font-roboto inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-none text-aspect text-xs xs:text-sm font-normal transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-neutral-950 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 dark:focus-visible:ring-neutral-300",
  {
    variants: {
      variant: {
        matchbus: "bg-matchbus text-secondary hover:bg-matchbus/80  ",
        default:
          "bg-neutral-900 text-neutral-50 shadow hover:bg-neutral-900/90 dark:bg-neutral-50 dark:text-neutral-900 dark:hover:bg-neutral-50/90",
        // "hover:brightness-95 bg-button transition-transform scale-[98%] hover:scale-100",
        submit:
          "hover:brightness-105 bg-matchbus transition-transform scale-[98%] hover:scale-100",
        cancel:
          "hover:brightness-95 bg-base transition-transform scale-[98%] hover:scale-100",
        submitRed: "rounded-2xl text-offWhite bg-action",
        "inline-link":
          "text-matchbus [font-size:inherit] hover:underline inline",
        destructive:
          "bg-red-500 text-neutral-50 shadow-sm hover:bg-red-500/90 dark:bg-red-900 dark:text-neutral-50 dark:hover:bg-red-900/90",
        outline:
          "border border-neutral-200 hover:border-matchbus text-secondary bg-white shadow-sm hover:bg-babyBlue hover:text-matchbus dark:border-secondary dark:bg-neutral-950 dark:hover:bg-neutral-800 dark:hover:text-neutral-50",
        secondary:
          "bg-neutral-100 text-neutral-900 shadow-sm hover:bg-neutral-100/80 dark:bg-neutral-800 dark:text-neutral-50 dark:hover:bg-neutral-800/80",
        ghost:
          "hover:bg-neutral-100 hover:text-neutral-900 dark:hover:bg-neutral-800 dark:hover:text-neutral-50",
        link: "text-matchbus",
        dark: "text-base bg-secondary ",
        icon: "bg-transparent text-secondary hover:scale-85",
      },
      size: {
        default: "h-auto px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs [&_svg]:size-4",
        lg: "h-10 rounded-md px-4 [&_svg]:size-5",
        xl: "h-12 rounded-md px-10 text-lg",
        xxl: "text-lg h-10 font-semibold md:font-bold px-4 py-2 rounded-lg md:h-12 md:px-4 md:text-xl xxl:h-16 xxl:px-8 xxl:text-2xl",
        icon: "h-9 w-9",
        inline: "h-content w-content p-0 m-0",
        xs: "h-5 w-5",
      },
    },
    defaultVariants: {
      variant: "matchbus",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export type ButtonVariant = NonNullable<
  VariantProps<typeof buttonVariants>["variant"]
>;
export type ButtonSize = NonNullable<
  VariantProps<typeof buttonVariants>["size"]
>;

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? SlotPrimitive.Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        type={props.type ?? (props.onClick ? "button" : "submit")}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
